/* eslint-disable */
import { gsap } from 'gsap';
import { isEmpty } from 'lodash';

export default class Cursor {
  /**
   * @constructor
   * @class Cursor
   * @classdesc Controls a custom cursor which allows extra interactions
   *
   * @param {object} options
   *  @param {object} [options.speed=0.6] Time in seconds for the custom cursor to catch up with the real cursor. Must be higher than 0.
   *  @param {string|HTMLElement|NodeList} [options.container=body] Container for the custom cursor
   *  @param {bool} [options.scrollSmoother=false] If ScrollSmoother is active
   *  @param {bool} [options.active=false] If the custom cursor is active
   */
  constructor(options = {}, scrollSmoother = false) {
    const defaultOptions = {
      speed: 0.6,
      container: 'body',
      active: false,
    };

    this.options = { ...defaultOptions, ...options, scrollSmoother };

    if (!this.options.active) return false;
    if (this.options.speed === 0) throw new Error('Cursor speed must be higher than 0.');

    this.createCursor();
    this.bindListeners();
  }

  /**
   * Creates and injects the cursor into the container
   */
  createCursor() {
    const { speed, container } = this.options;
    const cReference = gsap.utils.toArray(container);
    const cursor = document.createElement('div');
    const ball = document.createElement('div');
    const text = document.createElement('div');

    ball.classList.add('wb-cursor__ball');
    text.classList.add('wb-cursor__text');
    cursor.classList.add('wb-cursor');
    cursor.id = 'wb-cursor';
    cursor.appendChild(ball);
    cursor.appendChild(text);

    cReference[0]?.appendChild(cursor);

    gsap.set(cursor, { xPercent: -50, yPercent: -50 });
    this.xTo = gsap.quickTo(cursor, 'x', { duration: speed, ease: 'power3' });
    this.yTo = gsap.quickTo(cursor, 'y', { duration: speed, ease: 'power3' });
    this.el = cursor;
    this.text = text;
    this.ball = ball;
  }

  /**
   * Binds listeners
   */
  bindListeners() {
    window.addEventListener('mousemove', (e) => {
      if (this.stick?.el) {
        const bound = this.stick.el.getBoundingClientRect();

        this.stick = {
          el: this.stick.el,
          y: bound.top + this.stick.el.clientHeight / 2,
          x: bound.left + this.stick.el.clientWidth / 2,
        };
      }

      const stickX = this.magnetic ? this.stick?.x - (this.stick.x - e.clientX) * 0.15 : this.stick?.x;
      const stickY = this.magnetic ? this.stick?.y - (this.stick.y - e.clientY) * 0.15 : this.stick?.y;

      this.xTo(this.stick ? stickX : e.clientX);
      this.yTo(this.stick ? stickY : e.clientY);
    });

    document.documentElement.addEventListener('mouseleave', () => {
      this.el?.classList.add('wb-cursor--out');
    });

    document.documentElement.addEventListener('mouseenter', () => {
      this.el?.classList.remove('wb-cursor--out');
    });

    document.body.addEventListener(
      'mouseenter',
      (e) => {
        if (e.target.dataset.cursorState) {
          this.setState(e.target.dataset.cursorState);
        }
        if (e.target.dataset.cursorText) {
          this.setText(e.target.dataset.cursorText);
        }
        if (e.target.dataset.cursorStick) {
          this.setStick(e.target.dataset.cursorStick);
        }
        if (e.target.dataset.cursorMagnetic) {
          this.magnetic = true;
        }
      },
      true,
    );

    document.body.addEventListener(
      'mouseleave',
      (e) => {
        if (e.target.dataset.cursorState) {
          this.removeState(e.target.dataset.cursorState);
        }
        if (e.target.dataset.cursorText) {
          this.removeText(e.target.dataset.cursorText);
        }
        if (e.target.dataset.cursorStick) {
          this.removeStick();
        }
        if (e.target.dataset.cursorMagnetic) {
          this.magnetic = false;
        }
      },
      true,
    );
  }

  /**
   * Sets state
   * @param {string} state
   */
  setState(state) {
    this.el.classList.add(state);
  }

  /**
   * Removes state
   * @param {string} state
   */
  removeState(state) {
    this.el.classList.remove(state);
  }

  /**
   * Toggles state
   * @param {string} state
   */
  toggleState(state) {
    this.el.classList.toggle(state);
  }

  /**
   * Resets all states
   */
  resetState() {
    this.el.classList = 'wb-cursor';
  }

  /**
   * Sets cursor text
   * @param {string} text
   */
  setText(text) {
    this.text.innerHTML = text;
    this.el.classList.add('wb-cursor--text');
  }

  /**
   * Removes cursor text
   */
  removeText() {
    this.text.innerHTML = '';
    this.el.classList.remove('wb-cursor--text');
  }

  /**
   * Sets the sticky cursor on a specific element
   * @param {string|HTMLElement} el
   */
  setStick(el) {
    const target = gsap.utils.toArray(el);

    if (!isEmpty(target)) {
      const bound = target[0].getBoundingClientRect();

      this.stick = {
        el: target[0],
        y: bound.top + target[0].clientHeight / 2,
        x: bound.left + target[0].clientWidth / 2,
      };
      this.xTo.tween.timeScale(3);
      this.yTo.tween.timeScale(3);
      this.xTo(this.stick.x);
      this.yTo(this.stick.y);
    }
  }

  /**
   * Removes sticky cursor
   */
  removeStick() {
    this.stick = false;
    this.xTo.tween.timeScale(1);
    this.yTo.tween.timeScale(1);
  }

  /**
   * Resets cursor back to its original state
   */
  reset() {
    this.resetState();
    this.removeStick();
    this.removeText();
  }
}
